import { Badge } from '@/components/shadcn/badge'
import { ContentDataType, FieldDefinition } from '../types-v6'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { DynamicIcon } from '@/components/icons/DynamicIcon'

// Define a map for Tailwind color classes
// This allows us to safely map color names to actual Tailwind classes
const colorMap: Record<string, string> = {
  gray: 'text-gray-500',
  red: 'text-red-500 border-red-500 bg-red-50',
  yellow: 'text-yellow-700 border-yellow-500 bg-yellow-50',
  green: 'text-green-700 border-green-500 bg-green-50',
  // Add more colors as needed
}

export const ItemHeaderLayout = ({
  headerFields,
  content,
}: {
  headerFields: FieldDefinition[]
  content: ContentDataType
}) => {
  const renderFieldValue = (field: FieldDefinition, content: ContentDataType) => {
    const propertyId = field.propertyId
    const value = content[propertyId]

    // If no value, return nothing
    if (value === undefined || value === null) {
      return null
    }

    // Get component type and metadata from field config
    const { component, metadata } = field.fieldConfig.layout

    // Handle Badge component
    if (component === 'Badge') {
      // Handle array of values for Badge
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((item, index) => {
              return (
                <Badge key={`${field.propertyId}-${index}`} variant="outline">
                  {String(item)}
                </Badge>
              )
            })}
          </div>
        )
      }

      // Handle single value for Badge
      const { color } = metadata?.[value as string] ?? {}
      const colorClass = color && colorMap[color] ? colorMap[color] : ''
      if (colorClass) {
        return (
          <Badge variant="outline" className={colorClass}>
            {String(value)}
          </Badge>
        )
      }

      return (
        <Badge variant="outline" className="text-xs">
          {String(value)}
        </Badge>
      )
    }

    // Handle Icon component with metadata
    if (component === 'Icon' && metadata && typeof value === 'string' && metadata[value]) {
      const { color, icon } = metadata[value]

      // Use our color map to get the appropriate Tailwind class
      const colorClass = color && colorMap[color] ? colorMap[color] : ''

      // Create a tooltip that displays the field title and value
      // const tooltipText = `${field.title}: ${value}`
      // const tooltipText = value

      if (!icon) {
        return null
      }

      // Return just the icon wrapped in a tooltip, without the label
      return (
        <TooltipButton tooltip={value} contentClassName="bg-foreground text-xs text-offwhite">
          <div className="flex items-center">
            <DynamicIcon
              iconName={icon}
              size={16}
              className={colorClass}
              fallback={<span className="text-xs">{value}</span>}
            />
          </div>
        </TooltipButton>
      )
    }

    // Default rendering for other values
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return <span className="text-xs text-muted-foreground">{String(value)}</span>
    }

    // For complex objects, return null or a simplified representation
    return null
  }

  // Separate fields by component type
  const badgeFields = headerFields.filter((field) => field.fieldConfig.layout.component === 'Badge')
  const iconFields = headerFields.filter((field) => field.fieldConfig.layout.component === 'Icon')
  const otherFields = headerFields.filter(
    (field) => field.fieldConfig.layout.component !== 'Badge' && field.fieldConfig.layout.component !== 'Icon'
  )

  return (
    <div className="flex flex-wrap items-center gap-x-2">
      {/* Render Badge components first */}
      {badgeFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2">
          {badgeFields.map((field) => (
            <div key={field.propertyId} className="flex items-center">
              {renderFieldValue(field, content)}
            </div>
          ))}
        </div>
      )}

      {/* Render divider if both badge and icon fields exist */}
      {badgeFields.length > 0 && iconFields.length > 0 && <div className="mx-1 h-4 w-px bg-gray-200" />}

      {/* Render Icon components second */}
      {iconFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2">
          {iconFields.map((field) => (
            <div key={field.propertyId} className="flex items-center">
              {renderFieldValue(field, content)}
            </div>
          ))}
        </div>
      )}

      {/* Render divider if other fields exist and we have either badges or icons */}
      {otherFields.length > 0 && (badgeFields.length > 0 || iconFields.length > 0) && (
        <div className="mx-1 h-4 w-px bg-gray-200" />
      )}

      {/* Render any other components last */}
      {otherFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2">
          {otherFields.map((field) => (
            <div key={field.propertyId} className="flex items-center">
              {renderFieldValue(field, content)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
